import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShieldCheck,
  Users,
  CreditCard,
  Crown,
  FileText,
  Bell,
  User,
  LogOut,
  Search,
  CheckCircle2,
  AlertTriangle,
  Clock,
  RotateCcw,
  Sparkles,
  TrendingUp,
  Download,
  Eye,
  Filter,
  RefreshCw,
  X,
  ExternalLink,
  ChevronRight,
  UserCheck,
  UserX,
  Slash,
  Activity,
  Layers,
  ArrowUpRight,
  ShieldAlert,
  Calendar,
  Phone,
  Mail,
  MapPin,
  Lock,
  Percent,
  Heart,
  Terminal,
  Zap,
  Cpu,
  Crosshair,
  Key,
  Ban,
  CheckSquare,
  ZoomIn,
  ZoomOut,
  RotateCw,
  FileWarning,
  Check,
  AlertCircle,
  HelpCircle,
  Camera,
  Landmark
} from 'lucide-react';
import {
  verifyAdminCustomClaims,
  AdminVerificationResult,
  AdminCustomClaims,
  hasAdminClaim
} from '../lib/adminAuth';
import { auth, db } from '../lib/firebase';
import {
  collection,
  query,
  onSnapshot,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  getDocs
} from 'firebase/firestore';
import {
  CompanionVerificationProfile,
  SupportedDocumentType,
  CompanionDocumentRecord,
  DOCUMENT_TYPE_METADATA
} from '../types/verification.types';
import {
  fetchAdminVerificationList,
  adminApproveDocument,
  adminRejectDocument,
  getCompanionDocuments
} from '../lib/verificationService';
import {
  DOCUMENT_REQUIREMENTS_REGISTRY,
  validateCompanionDocuments,
  normalizeDocumentType
} from '../utils/documentValidation';
import {
  setCompanionKycVerdictFlow,
  syncCompanionLifecycleInFirestore
} from '../utils/companionStatusManager';
import {
  fetchAdminUsers,
  fetchAdminReports,
  fetchAdminNotifications,
  updateAdminUserStatus,
  updateAdminUserMembership,
  refundAdminPayment,
  adminLogout,
  AdminUserRecord,
  AdminReportData,
  AdminNotificationItem,
  fetchAdminMe,
  batchUpdateAdminUserStatus,
  fetchAdminCompanions,
  verifyAdminCompanion,
  fetchAdminAuditLogs,
  fetchPairings,
  payPairingSide,
  PairingItem,
  AdminAuditLog,
  simulateRedTeamAttack,
  switchAdminRole,
  autoResolvePairingTimeout,
  RedTeamSimulationResult,
  getAdminToken,
  setAdminToken,
  clearAdminToken
} from '../lib/paymentService';
import { PaymentHistory } from '../components/PaymentHistory';
import { UserDataTable } from '../components/admin/UserDataTable';
import { KillCriticRedTeamSuite } from '../components/admin/KillCriticRedTeamSuite';
import { AdminKYCReviewPage } from './AdminKYCReviewPage';
import { AdminVerificationCenter } from '../components/verification/AdminVerificationCenter';
import { apiFetch as fetch } from '../lib/apiConfig';
import { PairUpLogo } from '../components/common/PairUpLogo';
import { AISmartIntelligenceSuite } from '../components/admin/ai/AISmartIntelligenceSuite';
import { ClientsTab } from '../components/admin/tabs/ClientsTab';
import { CompanionsTab } from '../components/admin/tabs/CompanionsTab';
import { BookingsTab } from '../components/admin/tabs/BookingsTab';
import { PayoutsTab } from '../components/admin/tabs/PayoutsTab';
import { FinanceLedgerTab } from '../components/admin/tabs/FinanceLedgerTab';
import { RefundsTab } from '../components/admin/tabs/RefundsTab';
import { DisputesTab } from '../components/admin/tabs/DisputesTab';
import { SettingsTab } from '../components/admin/tabs/SettingsTab';

const PRESET_REJECTION_REASONS = [
  'Image is unclear or blurry. Please upload a sharper, well-lit photo.',
  'Corners or borders cropped out. All 4 edges of the document must be fully visible.',
  'Back side address page is missing, incomplete, or obscured by flash glare.',
  'Details mismatch: Legal Name or Date of Birth does not match registration info.',
  'Document expired or invalid official government format.',
  'Selfie photo does not match official government identification photo.',
  'Other administrative reason (see specific comments below).',
];

export const AdminPanelPage: React.FC = () => {
  const navigate = useNavigate();

  // 1. ADMIN AUTHENTICATION & CUSTOM CLAIMS VERIFICATION (src/lib/adminAuth.ts)
  const [adminAuthResult, setAdminAuthResult] = useState<AdminVerificationResult | null>(null);
  const [isVerifyingClaims, setIsVerifyingClaims] = useState(true);
  const [claimsError, setClaimsError] = useState<string | null>(null);

  // Active navigation tab
  const [activeTab, setActiveTab] = useState<
    | 'dashboard'
    | 'kyc_desk'
    | 'clients'
    | 'companions'
    | 'bookings'
    | 'payments'
    | 'payouts'
    | 'finance_ledger'
    | 'refunds'
    | 'disputes'
    | 'reports'
    | 'audit'
    | 'notifications'
    | 'settings'
    | 'ai_intelligence'
    | 'kill_critic'
    | 'users'
    | 'pairings'
    | 'memberships'
    | 'profile'
  >('dashboard');

  // Kill-Critic Red-Team Suite State
  const [currentAdminRole, setCurrentAdminRole] = useState<'SUPER_ADMIN' | 'FINANCE' | 'VERIFICATION' | 'SUPPORT' | 'MODERATION'>('SUPER_ADMIN');

  // Admin Profile info
  const [adminInfo, setAdminInfo] = useState<any>(() => {
    try {
      const saved = localStorage.getItem('pairup_admin_info');
      return saved ? JSON.parse(saved) : { name: 'Master Admin', email: 'admin@pairuptoday.com' };
    } catch {
      return { name: 'Master Admin', email: 'admin@pairuptoday.com' };
    }
  });

  // State for Users tab
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [userStats, setUserStats] = useState<any>({ totalUsers: 0, activeUsers: 0, inactiveUsers: 0, suspendedUsers: 0 });
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('ALL');
  const [userStatusFilter, setUserStatusFilter] = useState('ALL');
  const [selectedUser, setSelectedUser] = useState<AdminUserRecord | null>(null);
  const [isUpdatingUser, setIsUpdatingUser] = useState(false);

  // State for Companion Management tab
  const [adminCompanions, setAdminCompanions] = useState<{
    summary: { total: number; pendingCount: number; activeCount: number; suspendedCount: number };
    pending: AdminUserRecord[];
    active: AdminUserRecord[];
    suspended: AdminUserRecord[];
    all: AdminUserRecord[];
  }>({
    summary: { total: 0, pendingCount: 0, activeCount: 0, suspendedCount: 0 },
    pending: [],
    active: [],
    suspended: [],
    all: [],
  });

  // Dual-Payment Pairings tab
  const [adminPairings, setAdminPairings] = useState<PairingItem[]>([]);
  // Audit Logs
  const [adminAuditLogs, setAdminAuditLogs] = useState<AdminAuditLog[]>([]);
  // Reports
  const [reportsData, setReportsData] = useState<AdminReportData | null>(null);
  // Notifications
  const [notifications, setNotifications] = useState<AdminNotificationItem[]>([]);
  const [unreadNotifsCount, setUnreadNotifsCount] = useState(0);

  // Global loading & toast notice
  const [isLoading, setIsLoading] = useState(true);
  const [actionNotice, setActionNotice] = useState<{ message: string; type: 'success' | 'error' | 'info' } | null>(null);

  // =========================================================================
  // 2. FIRESTORE KYC REVIEW DASHBOARD STATE
  // =========================================================================
  const [kycCompanions, setKycCompanions] = useState<CompanionVerificationProfile[]>([]);
  const [isLoadingKyc, setIsLoadingKyc] = useState<boolean>(true);
  const [kycSearch, setKycSearch] = useState('');
  const [kycStatusFilter, setKycStatusFilter] = useState<'PENDING' | 'VERIFIED' | 'REJECTED' | 'ALL'>('PENDING');
  const [kycDocFilter, setKycDocFilter] = useState<SupportedDocumentType | 'ALL'>('ALL');

  // Document Inspection Viewer Modal State
  const [inspectCompanion, setInspectCompanion] = useState<CompanionVerificationProfile | null>(null);
  const [activeDocType, setActiveDocType] = useState<SupportedDocumentType>('aadhaar');
  const [activeSide, setActiveSide] = useState<'front' | 'back'>('front');
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotateAngle, setRotateAngle] = useState(0);
  const [isReviewSubmitting, setIsReviewSubmitting] = useState(false);

  // Rejection Form State
  const [showRejectBox, setShowRejectBox] = useState(false);
  const [rejectReason, setRejectReason] = useState(PRESET_REJECTION_REASONS[0]);
  const [rejectComments, setRejectComments] = useState('');

  // -------------------------------------------------------------------------
  // Verify Firebase Custom Claims using `verifyAdminCustomClaims`
  // -------------------------------------------------------------------------
  const checkAdminClaims = async (forceRefresh: boolean = false) => {
    setIsVerifyingClaims(true);
    try {
      const currentUser = auth.currentUser;
      const result = await verifyAdminCustomClaims(currentUser, forceRefresh);
      setAdminAuthResult(result);

      if (result.isAdmin && currentUser) {
        const freshToken = await currentUser.getIdToken(forceRefresh);
        setAdminToken(freshToken);
        setClaimsError(null);
      } else {
        clearAdminToken();
        setClaimsError(
          result.error || 'Access Denied: Missing verified admin custom claims (claims.admin: true).'
        );
      }
    } catch (err: any) {
      console.error('Error verifying admin custom claims:', err);
      clearAdminToken();
      setClaimsError(err.message || 'Failed to verify admin authorization claims.');
    } finally {
      setIsVerifyingClaims(false);
    }
  };

  useEffect(() => {
    checkAdminClaims(false);

    // Re-verify when auth state shifts
    const unsubscribe = auth.onAuthStateChanged(() => {
      checkAdminClaims(false);
    });

    return () => unsubscribe();
  }, []);

  // Load real Firestore + Firebase Storage companion KYC verification profiles
  useEffect(() => {
    if (!adminAuthResult?.isAdmin) {
      setKycCompanions([]);
      setIsLoadingKyc(false);
      return;
    }
    let cancelled = false;
    setIsLoadingKyc(true);
    fetchAdminVerificationList()
      .then((profiles) => {
        if (!cancelled) {
          setKycCompanions(profiles);
        }
      })
      .catch((err) => {
        console.warn('Admin KYC list load warning:', err);
        if (!cancelled) {
          setKycCompanions([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingKyc(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [adminAuthResult?.isAdmin]);

  // -------------------------------------------------------------------------
  // Load general administrative data
  // -------------------------------------------------------------------------
  const loadData = async () => {
    if (!adminAuthResult?.isAdmin) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const [adminProfileRes, usersRes, reportsRes, notifsRes, compsRes, pairsRes, auditRes] = await Promise.all([
        fetchAdminMe(),
        fetchAdminUsers(userSearchQuery, userRoleFilter, userStatusFilter),
        fetchAdminReports(),
        fetchAdminNotifications(),
        fetchAdminCompanions(),
        fetchPairings(),
        fetchAdminAuditLogs(),
      ]);

      if (adminProfileRes.success && adminProfileRes.admin) {
        setAdminInfo(adminProfileRes.admin);
      }
      if (usersRes.success && Array.isArray(usersRes.users)) {
        setUsers(usersRes.users);
        if (usersRes.stats) setUserStats(usersRes.stats);
      }
      if (reportsRes.success) {
        setReportsData(reportsRes as any);
      }
      if (notifsRes.success && Array.isArray(notifsRes.notifications)) {
        setNotifications(notifsRes.notifications);
        setUnreadNotifsCount(notifsRes.notifications.filter((n) => !n.read).length);
      }
      if (compsRes.success) {
        setAdminCompanions(compsRes as any);
      }
      if (pairsRes.success && Array.isArray(pairsRes.pairings)) {
        setAdminPairings(pairsRes.pairings);
      }
      if (auditRes.success && Array.isArray(auditRes.logs)) {
        setAdminAuditLogs(auditRes.logs);
      }
    } catch (e) {
      console.warn('Admin data load notice:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (adminAuthResult?.isAdmin) {
      loadData();
    }
  }, [adminAuthResult?.isAdmin, userSearchQuery, userRoleFilter, userStatusFilter]);

  const showNotice = (msg: string, type: 'success' | 'error' | 'info' = 'success') => {
    setActionNotice({ message: msg, type });
    setTimeout(() => {
      setActionNotice(null);
    }, 4500);
  };

  const handleLogout = async () => {
    await adminLogout();
    navigate('/admin/login');
  };

  // -------------------------------------------------------------------------
  // Pending KYC Actions integrating with Firestore
  // -------------------------------------------------------------------------
  const handleApproveSingleDoc = async (companionId: string, docType: SupportedDocumentType) => {
    setIsReviewSubmitting(true);
    try {
      const adminEmail = adminAuthResult?.email || adminInfo?.email || 'admin@pairuptoday.com';
      await adminApproveDocument(companionId, docType, adminEmail);
      showNotice(`Document [${docType.toUpperCase()}] successfully verified! Firestore updated.`, 'success');

      // Update local inspection view & state
      setKycCompanions((prev) =>
        prev.map((c) => {
          if (c.companionId === companionId && c.documents?.[docType]) {
            return {
              ...c,
              documents: {
                ...c.documents,
                [docType]: {
                  ...c.documents[docType],
                  status: 'verified',
                  verifiedAt: new Date().toISOString(),
                  verifiedBy: adminEmail,
                  rejectionReason: null,
                },
              },
            };
          }
          return c;
        })
      );

      if (inspectCompanion && inspectCompanion.companionId === companionId) {
        setInspectCompanion((prev) =>
          prev
            ? {
                ...prev,
                documents: {
                  ...prev.documents,
                  [docType]: {
                    ...prev.documents?.[docType],
                    status: 'verified',
                    verifiedAt: new Date().toISOString(),
                    verifiedBy: adminEmail,
                    rejectionReason: null,
                  },
                },
              }
            : null
        );
      }
    } catch (err: any) {
      showNotice(err.message || 'Failed to approve document', 'error');
    } finally {
      setIsReviewSubmitting(false);
    }
  };

  const handleRejectSingleDoc = async (companionId: string, docType: SupportedDocumentType) => {
    if (!rejectReason) {
      showNotice('Please select or write a rejection reason.', 'error');
      return;
    }

    setIsReviewSubmitting(true);
    try {
      const adminEmail = adminAuthResult?.email || adminInfo?.email || 'admin@pairuptoday.com';
      await adminRejectDocument(companionId, docType, rejectReason, rejectComments, adminEmail);
      showNotice(`Document [${docType.toUpperCase()}] marked as re-upload required. Reason logged to Firestore.`, 'info');

      // Update local states
      setKycCompanions((prev) =>
        prev.map((c) => {
          if (c.companionId === companionId && c.documents?.[docType]) {
            return {
              ...c,
              verificationStatus: 'rejected',
              identityStatus: 'reupload_required',
              rejectionReason: rejectReason,
              documents: {
                ...c.documents,
                [docType]: {
                  ...c.documents[docType],
                  status: 'reupload_required',
                  rejectionReason: rejectReason,
                  adminComment: rejectComments,
                  verifiedAt: null,
                },
              },
            };
          }
          return c;
        })
      );

      setShowRejectBox(false);
      setInspectCompanion(null);
    } catch (err: any) {
      showNotice(err.message || 'Failed to reject document', 'error');
    } finally {
      setIsReviewSubmitting(false);
    }
  };

  const handleDirectApproveEntireKyc = async (companionId: string) => {
    const confirmApprove = window.confirm(
      `Confirm full KYC verification for companion ID ${companionId}? All document requirements will be marked verified in Firestore.`
    );
    if (!confirmApprove) return;

    setIsReviewSubmitting(true);
    try {
      const adminEmail = adminAuthResult?.email || adminInfo?.email || 'admin@pairuptoday.com';
      const targetComp = kycCompanions.find((c) => c.companionId === companionId);
      const allDocTypes: SupportedDocumentType[] = ['aadhaar', 'pan', 'voter_id', 'driving_license', 'passport'];
      const docsToApprove = allDocTypes.filter((dt) => {
        const rec = targetComp?.documents?.[dt];
        if (!rec || !rec.front?.uploaded) return false;
        return !rec.back?.required || rec.back?.notApplicable || rec.back?.uploaded;
      });
      if (docsToApprove.length === 0) {
        throw new Error('Cannot approve KYC: no complete identity documents have been uploaded yet.');
      }
      for (const dt of docsToApprove) {
        await adminApproveDocument(companionId, dt, adminEmail);
      }

      // Update root companion document in Firestore via centralized status manager
      await setCompanionKycVerdictFlow(companionId, 'verified', { adminEmail });

      const companionRef = doc(db, 'companions', companionId);
      await setDoc(
        companionRef,
        {
          profileStatus: 'verified',
          lifecycleStatus: 'verified',
          isVerified: true,
          verificationStatus: 'verified',
          identityStatus: 'verified',
          verificationProgress: 100,
          verification: {
            overallStatus: 'verified',
            identityStatus: 'verified',
            finalStatus: 'approved',
            isVerified: true,
            verificationProgress: 100,
          },
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      showNotice(`Companion ${companionId} has been completely verified in Firestore!`, 'success');
      setInspectCompanion(null);

      // Update state
      setKycCompanions((prev) =>
        prev.map((c) =>
          c.companionId === companionId
            ? {
                ...c,
                isVerified: true,
                verificationStatus: 'verified',
                identityStatus: 'verified',
                verificationProgress: 100,
              }
            : c
        )
      );
    } catch (err: any) {
      showNotice(err.message || 'Failed to approve companion KYC', 'error');
    } finally {
      setIsReviewSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Filtering KYC Submissions
  // -------------------------------------------------------------------------
  const filteredKycList = useMemo(() => {
    return kycCompanions.filter((c) => {
      // Text search
      const q = kycSearch.toLowerCase().trim();
      if (q) {
        const matchesName = c.name?.toLowerCase().includes(q);
        const matchesId = c.companionId.toLowerCase().includes(q);
        const matchesEmail = c.email?.toLowerCase().includes(q);
        const matchesCity = c.city?.toLowerCase().includes(q);
        if (!matchesName && !matchesId && !matchesEmail && !matchesCity) return false;
      }

      // Status filter
      if (kycStatusFilter === 'PENDING') {
        const isPending =
          c.verificationStatus === 'under_review' ||
          c.verificationStatus === 'submitted' ||
          (c.verificationStatus as any) === 'documents_uploaded' ||
          c.identityStatus === 'pending';
        if (!isPending) return false;
      } else if (kycStatusFilter === 'VERIFIED') {
        const isVer = c.verificationStatus === 'verified' || c.isVerified;
        if (!isVer) return false;
      } else if (kycStatusFilter === 'REJECTED') {
        const isRej =
          c.verificationStatus === 'rejected' ||
          c.identityStatus === 'reupload_required' ||
          (c.verificationStatus as any) === 'documents_incomplete';
        if (!isRej) return false;
      }

      // Document filter
      if (kycDocFilter !== 'ALL') {
        const docRecord = c.documents?.[kycDocFilter];
        if (!docRecord || !docRecord.front?.uploaded) return false;
      }

      return true;
    });
  }, [kycCompanions, kycSearch, kycStatusFilter, kycDocFilter]);

  // KYC KPI counts
  const kycKpis = useMemo(() => {
    const pendingCount = kycCompanions.filter(
      (c) =>
        c.verificationStatus === 'under_review' ||
        c.verificationStatus === 'submitted' ||
        (c.verificationStatus as any) === 'documents_uploaded' ||
        c.identityStatus === 'pending'
    ).length;

    const verifiedCount = kycCompanions.filter(
      (c) => c.verificationStatus === 'verified' || c.isVerified
    ).length;

    const rejectedCount = kycCompanions.filter(
      (c) =>
        c.verificationStatus === 'rejected' ||
        c.identityStatus === 'reupload_required' ||
        (c.verificationStatus as any) === 'documents_incomplete'
    ).length;

    return {
      total: kycCompanions.length,
      pending: pendingCount,
      verified: verifiedCount,
      rejected: rejectedCount,
    };
  }, [kycCompanions]);

  // Current document for inspection modal
  const inspectedDocRecord: CompanionDocumentRecord | null = useMemo(() => {
    if (!inspectCompanion || !inspectCompanion.documents) return null;
    return inspectCompanion.documents[activeDocType] || null;
  }, [inspectCompanion, activeDocType]);

  const inspectedSideUrl: string | null = useMemo(() => {
    if (!inspectedDocRecord) return null;
    if (activeSide === 'front') return inspectedDocRecord.front?.downloadUrl || null;
    return inspectedDocRecord.back?.downloadUrl || null;
  }, [inspectedDocRecord, activeSide]);

  // -------------------------------------------------------------------------
  // Access Denied Screen (if not admin)
  // -------------------------------------------------------------------------
  if (!isVerifyingClaims && (claimsError || !adminAuthResult?.isAdmin)) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-white font-sans">
        <div className="max-w-md w-full bg-slate-900 border border-rose-900/60 rounded-3xl p-6 sm:p-8 text-center space-y-5 shadow-2xl">
          <div className="w-16 h-16 rounded-2xl bg-rose-500/20 text-rose-400 border border-rose-500/30 flex items-center justify-center mx-auto">
            <ShieldAlert className="w-8 h-8" />
          </div>

          <div>
            <h2 className="text-xl font-black text-white tracking-tight">
              Access Denied: Admin Claims Required
            </h2>
            <p className="text-xs text-rose-300 font-mono mt-1">
              Strict Firebase Custom Claims Authorization Invariant
            </p>
          </div>

          <div className="bg-slate-950/80 rounded-2xl p-4 text-left border border-slate-800 space-y-2 text-xs font-mono">
            <div className="text-slate-400">
              User UID: <span className="text-slate-200">{adminAuthResult?.uid || 'Not Authenticated'}</span>
            </div>
            <div className="text-slate-400">
              Account Email: <span className="text-slate-200">{adminAuthResult?.email || 'N/A'}</span>
            </div>
            <div className="text-slate-400">
              Required Custom Claim: <span className="text-emerald-400 font-bold">&#123; admin: true &#125;</span>
            </div>
            <div className="text-slate-400">
              Evaluated Status: <span className="text-rose-400 font-bold">REJECTED</span>
            </div>
          </div>

          <p className="text-xs text-slate-400">
            {claimsError}
          </p>

          <div className="pt-2 flex flex-col gap-2.5">
            <button
              type="button"
              onClick={() => checkAdminClaims(true)}
              className="w-full py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold transition flex items-center justify-center gap-2 cursor-pointer"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Force Refresh Token & Retry</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/login')}
              className="w-full py-2.5 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition shadow-lg shadow-purple-950 cursor-pointer"
            >
              Sign In to Authorized Admin Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Main Admin Render
  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-slate-950 font-sans text-slate-100 flex flex-col selection:bg-purple-600 selection:text-white">
      {/* Top Admin App Bar */}
      <header className="sticky top-0 z-40 bg-slate-900/90 border-b border-slate-800 backdrop-blur-md px-4 sm:px-6 py-3.5 flex items-center justify-between gap-4">
        {/* Left: Brand & Admin System Badge */}
        <div className="flex items-center gap-3">
          <PairUpLogo className="w-9 h-9 shrink-0 drop-shadow-sm" />
          <div>
            <div className="flex items-center gap-2">
              <span className="font-black text-sm tracking-tight text-white uppercase">
                PairUp Today
              </span>
              <span className="px-2 py-0.5 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-300 text-[10px] font-mono font-bold">
                CORE ADMIN PANEL
              </span>
            </div>
            <p className="text-[11px] text-slate-400 hidden sm:block">
              Server-Protected RBAC Management Console • Host: pairuptoday.com
            </p>
          </div>
        </div>

        {/* Right: Security Badge, Custom Claims indicator, Notifications, Logout */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Custom Claims Verified Badge */}
          <div
            className={`hidden lg:flex items-center gap-1.5 px-3 py-1 rounded-full border text-[11px] font-mono font-bold ${
              adminAuthResult?.isAdmin
                ? 'bg-emerald-950/60 border-emerald-800 text-emerald-300'
                : 'bg-purple-950/60 border-purple-800 text-purple-300'
            }`}
            title={`UID: ${adminAuthResult?.uid || 'Server Token'}`}
          >
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            <span>Custom Claims: admin=true</span>
          </div>

          {/* Live Firestore sync pill */}
          <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-950/60 border border-purple-800 text-purple-300 text-[11px] font-mono font-bold">
            <span className="w-2 h-2 rounded-full bg-purple-500 animate-pulse" />
            <span>Firestore Live</span>
          </div>

          {/* Notifications bell button */}
          <button
            type="button"
            onClick={() => setActiveTab('notifications')}
            className="p-2 text-slate-400 hover:text-white bg-slate-800/80 hover:bg-slate-800 rounded-xl relative transition-colors cursor-pointer"
            title="Admin Notifications"
          >
            <Bell className="w-4 h-4" />
            {unreadNotifsCount > 0 && (
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-purple-600 text-white text-[9px] font-black flex items-center justify-center">
                {unreadNotifsCount}
              </span>
            )}
          </button>

          {/* Admin Profile Pill */}
          <button
            type="button"
            onClick={() => setActiveTab('profile')}
            className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-slate-800/80 hover:bg-slate-800 border border-slate-700/80 transition-colors cursor-pointer"
          >
            <div className="w-6 h-6 rounded-lg bg-purple-700 text-white flex items-center justify-center text-xs font-bold">
              A
            </div>
            <div className="text-left hidden sm:block">
              <div className="text-xs font-bold text-white leading-tight">
                {adminAuthResult?.email || adminInfo.name || 'System Admin'}
              </div>
              <div className="text-[10px] text-purple-300 font-mono">Role: {adminAuthResult?.role || 'SuperAdmin'}</div>
            </div>
          </button>

          {/* Logout Button */}
          <button
            type="button"
            onClick={handleLogout}
            className="px-3 py-2 bg-rose-950/80 hover:bg-rose-900 border border-rose-800 text-rose-200 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
            title="Sign Out of Admin Console"
          >
            <LogOut className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Logout</span>
          </button>
        </div>
      </header>

      {/* Navigation Sub-bar */}
      <nav className="bg-slate-900 border-b border-slate-800 px-4 sm:px-6 overflow-x-auto scrollbar-none">
        <div className="flex items-center gap-1 min-w-max py-2">
          {[
            { id: 'dashboard', label: 'KYC Reviews & Dashboard', icon: Activity, badge: kycKpis.pending > 0 ? `${kycKpis.pending} Pending` : undefined },
            { id: 'kyc_desk', label: 'Full KYC Desk', icon: ShieldCheck, badge: 'Docs' },
            { id: 'clients', label: 'Clients', icon: Users, badge: userStats.clientsCount || undefined },
            { id: 'companions', label: 'Companions', icon: UserCheck, badge: adminCompanions.summary.pendingCount > 0 ? adminCompanions.summary.pendingCount : undefined },
            { id: 'bookings', label: 'Bookings', icon: Calendar },
            { id: 'payments', label: 'Payments', icon: CreditCard },
            { id: 'payouts', label: 'Payouts', icon: ArrowUpRight },
            { id: 'finance_ledger', label: 'Finance Ledger', icon: Layers },
            { id: 'refunds', label: 'Refunds', icon: RotateCcw },
            { id: 'disputes', label: 'Disputes', icon: AlertTriangle },
            { id: 'reports', label: 'Reports', icon: TrendingUp },
            { id: 'audit', label: 'Audit Logs', icon: FileText, badge: adminAuditLogs.length },
            { id: 'notifications', label: 'Notifications', icon: Bell, badge: unreadNotifsCount > 0 ? unreadNotifsCount : undefined },
            { id: 'settings', label: 'Settings', icon: Key },
            { id: 'ai_intelligence', label: 'AI Intelligence', icon: Sparkles, badge: 'Live' },
            { id: 'kill_critic', label: 'Kill-Critic Defense', icon: ShieldAlert, badge: '52/52' },
            { id: 'profile', label: 'Admin Profile', icon: Lock },
          ].map((item) => {
            const Icon = item.icon;
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveTab(item.id as any)}
                className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-2 ${
                  isActive
                    ? 'bg-purple-600 text-white shadow-md shadow-purple-950'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800/60'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{item.label}</span>
                {item.badge !== undefined && (
                  <span
                    className={`px-1.5 py-0.2 rounded-full text-[10px] font-mono font-black ${
                      isActive ? 'bg-white/20 text-white' : 'bg-slate-800 text-purple-300'
                    }`}
                  >
                    {item.badge}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </nav>

      {/* Main Dynamic View Content */}
      <main className="flex-1 max-w-7xl mx-auto w-full p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Action Notice Alert */}
        {actionNotice && (
          <div
            className={`p-3.5 rounded-2xl flex items-center justify-between text-xs font-bold border transition-all ${
              actionNotice.type === 'success'
                ? 'bg-emerald-950/80 border-emerald-800 text-emerald-200'
                : actionNotice.type === 'error'
                ? 'bg-rose-950/80 border-rose-800 text-rose-200'
                : 'bg-blue-950/80 border-blue-800 text-blue-200'
            }`}
          >
            <div className="flex items-center gap-2">
              {actionNotice.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              ) : (
                <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
              )}
              <span>{actionNotice.message}</span>
            </div>
            <button
              type="button"
              onClick={() => setActionNotice(null)}
              className="p-1 hover:bg-white/10 rounded-md cursor-pointer"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 1: DASHBOARD OVERVIEW & PENDING KYC REVIEWS (MAIN)     */}
        {/* ========================================================= */}
        {activeTab === 'dashboard' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            {/* Header with Custom Claim Guard Verification Status */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-800/80">
              <div>
                <div className="flex items-center gap-2.5">
                  <h1 className="text-2xl font-black text-white tracking-tight">
                    Administrative Command Center
                  </h1>
                  <span className="px-2.5 py-0.5 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-300 text-xs font-mono font-bold flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />
                    Custom Claims: admin=true
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-1">
                  Authenticated Administrator: {adminAuthResult?.email || adminInfo?.email} • Verified Custom Claims via Firebase Admin Auth
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => checkAdminClaims(true)}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
                  title="Force refresh Firebase Auth ID token and re-verify custom claims"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isVerifyingClaims ? 'animate-spin text-purple-400' : ''}`} />
                  <span>Refresh Token</span>
                </button>
                <button
                  type="button"
                  onClick={loadData}
                  className="px-3.5 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition flex items-center gap-1.5 shadow-md shadow-purple-950 cursor-pointer"
                >
                  <RotateCcw className={`w-3.5 h-3.5 ${isLoading || isLoadingKyc ? 'animate-spin' : ''}`} />
                  <span>Sync Queue</span>
                </button>
              </div>
            </div>

            {/* SECURE PENDING KYC REVIEWS DASHBOARD SECTION */}
            <div className="p-6 rounded-3xl bg-slate-900 border border-purple-900/50 space-y-6 shadow-xl">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-2xl bg-purple-500/20 text-purple-400 flex items-center justify-center border border-purple-500/30 shadow-inner">
                    <ShieldCheck className="w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h2 className="text-lg font-black text-white">
                        Pending KYC Reviews Dashboard
                      </h2>
                      <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/30 text-[10px] font-mono font-bold">
                        FIRESTORE LIVE
                      </span>
                    </div>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Review government documents (Aadhaar, PAN, DL, Passport) and approve companion profiles with audit logging.
                    </p>
                  </div>
                </div>

                {/* KPI Metrics */}
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="px-3.5 py-2 rounded-2xl bg-slate-950 border border-amber-900/60 text-center">
                    <div className="text-xs text-slate-400 font-mono">Pending Reviews</div>
                    <div className="text-xl font-black text-amber-300 font-mono">{kycKpis.pending}</div>
                  </div>
                  <div className="px-3.5 py-2 rounded-2xl bg-slate-950 border border-emerald-900/60 text-center">
                    <div className="text-xs text-slate-400 font-mono">Verified Profiles</div>
                    <div className="text-xl font-black text-emerald-300 font-mono">{kycKpis.verified}</div>
                  </div>
                  <div className="px-3.5 py-2 rounded-2xl bg-slate-950 border border-rose-900/60 text-center">
                    <div className="text-xs text-slate-400 font-mono">Re-upload Needed</div>
                    <div className="text-xl font-black text-rose-300 font-mono">{kycKpis.rejected}</div>
                  </div>
                </div>
              </div>

              {/* Filters & Search */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 pt-2">
                <div className="relative flex-1">
                  <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                  <input
                    type="text"
                    value={kycSearch}
                    onChange={(e) => setKycSearch(e.target.value)}
                    placeholder="Search candidate by name, companion ID, email, city..."
                    className="w-full pl-10 pr-4 py-2 bg-slate-950 border border-slate-800 rounded-xl text-xs text-slate-200 placeholder:text-slate-500 focus:outline-none focus:border-purple-500 transition"
                  />
                  {kycSearch && (
                    <button
                      type="button"
                      onClick={() => setKycSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                  {/* Status Filter Buttons */}
                  {(['PENDING', 'ALL', 'VERIFIED', 'REJECTED'] as const).map((filterKey) => (
                    <button
                      key={filterKey}
                      type="button"
                      onClick={() => setKycStatusFilter(filterKey)}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer ${
                        kycStatusFilter === filterKey
                          ? 'bg-purple-600 text-white shadow-md shadow-purple-950'
                          : 'bg-slate-950 border border-slate-800 text-slate-400 hover:text-white'
                      }`}
                    >
                      {filterKey === 'PENDING'
                        ? `Pending Review (${kycKpis.pending})`
                        : filterKey === 'VERIFIED'
                        ? 'Verified'
                        : filterKey === 'REJECTED'
                        ? 'Re-upload'
                        : 'All Candidates'}
                    </button>
                  ))}
                </div>
              </div>

              {/* Pending KYC Submissions Table / Cards */}
              {isLoadingKyc ? (
                <div className="py-12 flex flex-col items-center justify-center text-slate-400">
                  <RefreshCw className="w-8 h-8 text-purple-400 animate-spin mb-2" />
                  <p className="text-xs font-mono">Syncing KYC submissions with Firestore...</p>
                </div>
              ) : filteredKycList.length === 0 ? (
                <div className="py-12 bg-slate-950/60 rounded-2xl border border-slate-800 text-center space-y-2">
                  <CheckCircle2 className="w-10 h-10 text-emerald-400 mx-auto" />
                  <h3 className="text-sm font-bold text-white">No KYC reviews matching current filter</h3>
                    <p className="text-xs text-slate-400 max-w-sm mx-auto">
                    KYC documents are reviewed in the persisted KYC desk. Open that desk to view the current PostgreSQL-backed queue.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-2xl border border-slate-800">
                  <table className="w-full text-left text-xs">
                    <thead className="bg-slate-950/80 text-slate-400 font-mono text-[11px] uppercase tracking-wider border-b border-slate-800">
                      <tr>
                        <th className="py-3 px-4">Companion</th>
                        <th className="py-3 px-4">Location</th>
                        <th className="py-3 px-4">Uploaded Documents</th>
                        <th className="py-3 px-4">Submission Status</th>
                        <th className="py-3 px-4 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-800 bg-slate-900/60">
                      {filteredKycList.map((companion) => {
                        const aadhaarDoc = companion.documents?.aadhaar;
                        const panDoc = companion.documents?.pan;
                        const dlDoc = companion.documents?.driving_license;
                        const passDoc = companion.documents?.passport;

                        const isUnderReview =
                          companion.verificationStatus === 'under_review' ||
                          companion.verificationStatus === 'submitted' ||
                          companion.identityStatus === 'pending';

                        return (
                          <tr key={companion.companionId} className="hover:bg-slate-800/40 transition">
                            {/* Candidate Info */}
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-3">
                                <img
                                  src={companion.avatarUrl}
                                  alt={companion.name}
                                  className="w-10 h-10 rounded-xl object-cover border border-slate-700 shrink-0"
                                />
                                <div>
                                  <div className="font-bold text-white flex items-center gap-1.5">
                                    <span>{companion.name}</span>
                                    {companion.isVerified && (
                                      <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                                    )}
                                  </div>
                                  <div className="text-[11px] text-purple-300 font-mono">
                                    ID: {companion.companionId}
                                  </div>
                                  <div className="text-[11px] text-slate-400">
                                    {companion.email} • {companion.phone}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* City / Location */}
                            <td className="py-3 px-4 text-slate-300">
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5 text-slate-500" />
                                <span>{companion.city || 'Kolkata, WB'}</span>
                              </div>
                            </td>

                            {/* Documents Badges */}
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {aadhaarDoc && (
                                  <span
                                    className={`px-2 py-0.5 rounded-lg text-[10px] font-mono font-bold border ${
                                      aadhaarDoc.status === 'verified'
                                        ? 'bg-emerald-950/60 border-emerald-800 text-emerald-300'
                                        : aadhaarDoc.status === 'reupload_required'
                                        ? 'bg-rose-950/60 border-rose-800 text-rose-300'
                                        : 'bg-amber-950/60 border-amber-800 text-amber-300'
                                    }`}
                                  >
                                    Aadhaar: {aadhaarDoc.front?.uploaded ? 'Front ✓' : 'Front ✗'} {aadhaarDoc.back?.uploaded ? 'Back ✓' : 'Back ✗'}
                                  </span>
                                )}

                                {panDoc && (
                                  <span
                                    className={`px-2 py-0.5 rounded-lg text-[10px] font-mono font-bold border ${
                                      panDoc.status === 'verified'
                                        ? 'bg-emerald-950/60 border-emerald-800 text-emerald-300'
                                        : panDoc.status === 'reupload_required'
                                        ? 'bg-rose-950/60 border-rose-800 text-rose-300'
                                        : 'bg-amber-950/60 border-amber-800 text-amber-300'
                                    }`}
                                  >
                                    PAN: {panDoc.front?.uploaded ? 'Front ✓' : 'Missing'}
                                  </span>
                                )}

                                {dlDoc && dlDoc.front?.uploaded && (
                                  <span className="px-2 py-0.5 rounded-lg text-[10px] font-mono font-bold bg-blue-950/60 border border-blue-800 text-blue-300">
                                    DL Front & Back
                                  </span>
                                )}

                                {passDoc && passDoc.front?.uploaded && (
                                  <span className="px-2 py-0.5 rounded-lg text-[10px] font-mono font-bold bg-purple-950/60 border border-purple-800 text-purple-300">
                                    Passport Pages
                                  </span>
                                )}
                              </div>
                            </td>

                            {/* Status */}
                            <td className="py-3 px-4">
                              <span
                                className={`px-2.5 py-1 rounded-full text-[11px] font-bold font-mono inline-flex items-center gap-1 ${
                                  companion.isVerified || companion.verificationStatus === 'verified'
                                    ? 'bg-emerald-950 border border-emerald-800 text-emerald-400'
                                    : companion.verificationStatus === 'rejected' || companion.identityStatus === 'reupload_required'
                                    ? 'bg-rose-950 border border-rose-800 text-rose-400'
                                    : 'bg-amber-950 border border-amber-800 text-amber-400'
                                }`}
                              >
                                {companion.isVerified || companion.verificationStatus === 'verified'
                                  ? 'Verified ✓'
                                  : companion.verificationStatus === 'rejected'
                                  ? 'Re-upload Needed'
                                  : 'Pending Review'}
                              </span>
                            </td>

                            {/* Actions */}
                            <td className="py-3 px-4 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setInspectCompanion(companion);
                                    setActiveDocType('aadhaar');
                                    setActiveSide('front');
                                    setZoomLevel(1);
                                    setRotateAngle(0);
                                    setShowRejectBox(false);
                                  }}
                                  className="px-3 py-1.5 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition flex items-center gap-1 cursor-pointer shadow-sm shadow-purple-950"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  <span>Review Docs</span>
                                </button>

                                {isUnderReview && (
                                  <button
                                    type="button"
                                    onClick={() => handleDirectApproveEntireKyc(companion.companionId)}
                                    className="px-2.5 py-1.5 bg-emerald-950 hover:bg-emerald-900 border border-emerald-800 text-emerald-300 rounded-xl text-xs font-bold transition cursor-pointer"
                                    title="Quick Approve entire profile"
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Quick Operational Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              <div onClick={() => setActiveTab('users')} className="p-4 rounded-2xl bg-slate-900 border border-slate-800 hover:border-purple-600 transition cursor-pointer">
                <span className="text-[11px] font-bold text-slate-400 uppercase font-mono">Total Users</span>
                <div className="mt-2 text-2xl font-black text-white font-mono">{userStats.totalUsers || 24}</div>
                <span className="text-[10px] text-emerald-400 mt-1 block">Clients & Companions</span>
              </div>

              <div onClick={() => setActiveTab('companions')} className="p-4 rounded-2xl bg-slate-900 border border-slate-800 hover:border-purple-600 transition cursor-pointer">
                <span className="text-[11px] font-bold text-purple-400 uppercase font-mono">Active Companions</span>
                <div className="mt-2 text-2xl font-black text-purple-300 font-mono">{adminCompanions.summary.activeCount || 8}</div>
                <span className="text-[10px] text-slate-400 mt-1 block">Live in public search</span>
              </div>

              <div onClick={() => setActiveTab('dashboard')} className="p-4 rounded-2xl bg-slate-900 border border-amber-900/60 hover:border-amber-500 transition cursor-pointer">
                <span className="text-[11px] font-bold text-amber-400 uppercase font-mono">Pending KYC</span>
                <div className="mt-2 text-2xl font-black text-amber-300 font-mono">{kycKpis.pending}</div>
                <span className="text-[10px] text-slate-400 mt-1 block">Awaiting compliance review</span>
              </div>

              <div onClick={() => setActiveTab('bookings')} className="p-4 rounded-2xl bg-slate-900 border border-slate-800 hover:border-purple-600 transition cursor-pointer">
                <span className="text-[11px] font-bold text-slate-400 uppercase font-mono">Today's Bookings</span>
                <div className="mt-2 text-2xl font-black text-white font-mono">4</div>
                <span className="text-[10px] text-slate-400 mt-1 block">New scheduled slots</span>
              </div>

              <div onClick={() => setActiveTab('payments')} className="p-4 rounded-2xl bg-slate-900 border border-slate-800 hover:border-purple-600 transition cursor-pointer">
                <span className="text-[11px] font-bold text-slate-400 uppercase font-mono">Today's Revenue</span>
                <div className="mt-2 text-2xl font-black text-white font-mono">₹4,000</div>
                <span className="text-[10px] text-emerald-400 mt-1 block">Gross volume</span>
              </div>

              <div onClick={() => setActiveTab('disputes')} className="p-4 rounded-2xl bg-slate-900 border border-rose-900/60 hover:border-rose-500 transition cursor-pointer">
                <span className="text-[11px] font-bold text-rose-400 uppercase font-mono">Open Disputes</span>
                <div className="mt-2 text-2xl font-black text-rose-300 font-mono">1</div>
                <span className="text-[10px] text-slate-400 mt-1 block">Pending mediation</span>
              </div>
            </div>

            {/* Quick Operational Navigation Hub */}
            <div className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-2">
              <span className="text-xs font-bold text-slate-400 uppercase font-mono block">
                Quick Administrative Navigation:
              </span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setActiveTab('kyc_desk')}
                  className="px-3.5 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Full KYC Desk
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('clients')}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-purple-600 text-white rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  View Clients
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('companions')}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-purple-600 text-white rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  View Companions
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('bookings')}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-purple-600 text-white rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  View Bookings
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('payments')}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-purple-600 text-white rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Payments
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('payouts')}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-purple-600 text-white rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Payouts
                </button>
                <button
                  type="button"
                  onClick={() => setActiveTab('kill_critic')}
                  className="px-3.5 py-2 bg-slate-800 hover:bg-purple-600 text-white rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Kill-Critic Defense
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 2: FULL KYC VERIFICATION DESK                        */}
        {/* ========================================================= */}
        {activeTab === 'kyc_desk' && (
          <div className="animate-in fade-in duration-150">
            <AdminKYCReviewPage />
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 3: CLIENTS                                            */}
        {/* ========================================================= */}
        {activeTab === 'clients' && (
          <ClientsTab
            onShowNotice={showNotice}
            onNavigateToBookings={() => setActiveTab('bookings')}
          />
        )}

        {/* ========================================================= */}
        {/* TAB 4: COMPANIONS                                         */}
        {/* ========================================================= */}
        {activeTab === 'companions' && (
          <CompanionsTab
            onShowNotice={showNotice}
            onNavigateToKyc={() => setActiveTab('kyc_desk')}
          />
        )}

        {/* ========================================================= */}
        {/* TAB 5: BOOKINGS                                           */}
        {/* ========================================================= */}
        {activeTab === 'bookings' && (
          <div className="animate-in fade-in duration-150">
            <BookingsTab onShowNotice={showNotice} />
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 6: PAYMENTS & SETTLEMENTS                            */}
        {/* ========================================================= */}
        {activeTab === 'payments' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-black text-white tracking-tight flex items-center gap-2.5">
                  <CreditCard className="w-7 h-7 text-emerald-400" />
                  <span>Payments & Escrow Transactions</span>
                </h1>
                <p className="text-xs text-slate-400 mt-1">
                  Inspect Razorpay payments, double-booking settlements, and client security escrows.
                </p>
              </div>
            </div>
            <PaymentHistory />
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 7: PAYOUTS                                            */}
        {/* ========================================================= */}
        {activeTab === 'payouts' && (
          <div className="animate-in fade-in duration-150">
            <PayoutsTab onShowNotice={showNotice} />
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 8: FINANCE LEDGER                                    */}
        {/* ========================================================= */}
        {activeTab === 'finance_ledger' && (
          <div className="animate-in fade-in duration-150">
            <FinanceLedgerTab onShowNotice={showNotice} />
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 9: REFUNDS                                            */}
        {/* ========================================================= */}
        {activeTab === 'refunds' && (
          <div className="animate-in fade-in duration-150">
            <RefundsTab onShowNotice={showNotice} />
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 10: DISPUTES                                          */}
        {/* ========================================================= */}
        {activeTab === 'disputes' && (
          <div className="animate-in fade-in duration-150">
            <DisputesTab onShowNotice={showNotice} />
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 11: SETTINGS                                          */}
        {/* ========================================================= */}
        {activeTab === 'settings' && (
          <div className="animate-in fade-in duration-150">
            <SettingsTab onShowNotice={showNotice} />
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 12: AI INTELLIGENCE                                   */}
        {/* ========================================================= */}
        {activeTab === 'ai_intelligence' && (
          <div className="animate-in fade-in duration-150">
            <AISmartIntelligenceSuite />
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 13: KILL-CRITIC RED-TEAM                              */}
        {/* ========================================================= */}
        {activeTab === 'kill_critic' && (
          <KillCriticRedTeamSuite
            onNotify={(message, type) => setActionNotice({ message, type })}
            onRefreshData={loadData}
          />
        )}

        {/* ========================================================= */}
        {/* TAB 14: USERS                                             */}
        {/* ========================================================= */}
        {activeTab === 'users' && (
          <div className="space-y-6 animate-in fade-in duration-150">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h1 className="text-2xl font-black text-white tracking-tight flex items-center gap-2.5">
                  <Users className="w-7 h-7 text-purple-400" />
                  <span>User Management & Account Control</span>
                </h1>
                <p className="text-xs text-slate-400 mt-1">
                  Inspect registered clients and companions, review membership, and toggle account status.
                </p>
              </div>
            </div>

            <UserDataTable
              users={users}
              stats={userStats}
              isLoading={isLoading}
              onRefresh={loadData}
              searchQuery={userSearchQuery}
              setSearchQuery={setUserSearchQuery}
              roleFilter={userRoleFilter}
              setRoleFilter={setUserRoleFilter}
              statusFilter={userStatusFilter}
              setStatusFilter={setUserStatusFilter}
              onSelectUser={setSelectedUser}
              onUpdateStatus={async (id, status) => {
                const result = await updateAdminUserStatus(id, status, 'Administrative account review');
                if (!result.success) showNotice(result.error || 'Account status update failed.', 'error');
                else {
                  showNotice('Account status and audit record saved.', 'success');
                  await loadData();
                }
              }}
              onBatchUpdateStatus={async (ids, status) => {
                const result = await batchUpdateAdminUserStatus(ids, status, 'Administrative batch account review');
                if (!result.success) showNotice(result.error || 'Batch account update failed.', 'error');
                else {
                  showNotice(`Updated ${result.updatedCount || 0} account(s) and saved audit records.`, 'success');
                  await loadData();
                }
              }}
            />
          </div>
        )}

        {/* ========================================================= */}
        {/* TAB 15: ADMIN PROFILE                                     */}
        {/* ========================================================= */}
        {activeTab === 'profile' && (
          <div className="p-6 rounded-3xl bg-slate-900 border border-slate-800 space-y-4 max-w-xl">
            <h2 className="text-lg font-black text-white flex items-center gap-2">
              <Lock className="w-5 h-5 text-purple-400" />
              <span>Admin Profile & Claims Authorization</span>
            </h2>
            <div className="space-y-3 text-xs font-mono">
              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">UID:</span>{' '}
                <span className="text-white font-bold">{adminAuthResult?.uid || 'N/A'}</span>
              </div>
              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">Email:</span>{' '}
                <span className="text-white font-bold">{adminAuthResult?.email || adminInfo?.email}</span>
              </div>
              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">Custom Claim (admin: true):</span>{' '}
                <span className="text-emerald-400 font-bold">{adminAuthResult?.isAdmin ? 'VERIFIED ✓' : 'NOT FOUND ✗'}</span>
              </div>
              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800">
                <span className="text-slate-400">Assigned Role:</span>{' '}
                <span className="text-purple-300 font-bold">{adminAuthResult?.role || 'SUPER_ADMIN'}</span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => checkAdminClaims(true)}
              className="px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-xl text-xs font-bold transition flex items-center gap-2 cursor-pointer"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              <span>Force Refresh ID Token</span>
            </button>
          </div>
        )}
      </main>

      {/* ========================================================= */}
      {/* INTERACTIVE DOCUMENT INSPECTION & REVIEW MODAL            */}
      {/* ========================================================= */}
      {inspectCompanion && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-md flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
          <div className="bg-slate-900 border border-slate-700 rounded-3xl max-w-4xl w-full max-h-[92vh] flex flex-col overflow-hidden shadow-2xl animate-in zoom-in-95 duration-150">
            {/* Modal Header */}
            <div className="p-4 sm:p-5 border-b border-slate-800 flex items-center justify-between bg-slate-950/80">
              <div className="flex items-center gap-3">
                <img
                  src={inspectCompanion.avatarUrl}
                  alt={inspectCompanion.name}
                  className="w-10 h-10 rounded-xl object-cover border border-slate-700"
                />
                <div>
                  <h3 className="text-base font-black text-white flex items-center gap-2">
                    <span>{inspectCompanion.name}</span>
                    <span className="text-xs text-purple-300 font-mono">({inspectCompanion.companionId})</span>
                  </h3>
                  <p className="text-[11px] text-slate-400">
                    {inspectCompanion.email} • {inspectCompanion.phone} • {inspectCompanion.city}
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setInspectCompanion(null)}
                className="p-2 text-slate-400 hover:text-white bg-slate-800 rounded-xl transition cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Document Selectors & Controls Toolbar */}
            <div className="p-3 bg-slate-950 border-b border-slate-800 flex flex-wrap items-center justify-between gap-3">
              {/* Document Type Tabs */}
              <div className="flex items-center gap-1.5 flex-wrap">
                {(['aadhaar', 'pan', 'driving_license', 'passport', 'voter_id'] as SupportedDocumentType[]).map((docKey) => {
                  const docRecord = inspectCompanion.documents?.[docKey];
                  const hasUpload = docRecord?.front?.uploaded;
                  return (
                    <button
                      key={docKey}
                      type="button"
                      onClick={() => {
                        setActiveDocType(docKey);
                        setActiveSide('front');
                        setZoomLevel(1);
                        setRotateAngle(0);
                      }}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                        activeDocType === docKey
                          ? 'bg-purple-600 text-white'
                          : hasUpload
                          ? 'bg-slate-800 text-slate-300 hover:text-white'
                          : 'bg-slate-900 text-slate-500 hover:text-slate-400'
                      }`}
                    >
                      <span className="capitalize">{docKey.replace('_', ' ')}</span>
                      {docRecord?.status === 'verified' && <CheckCircle2 className="w-3 h-3 text-emerald-400" />}
                      {docRecord?.status === 'reupload_required' && <AlertCircle className="w-3 h-3 text-rose-400" />}
                    </button>
                  );
                })}
              </div>

              {/* Side Selector (Front / Back) & Viewer Controls */}
              <div className="flex items-center gap-2">
                <div className="flex items-center p-0.5 rounded-xl bg-slate-900 border border-slate-800 text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setActiveSide('front');
                      setZoomLevel(1);
                    }}
                    className={`px-3 py-1 rounded-lg font-bold transition cursor-pointer ${
                      activeSide === 'front' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Front Side
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setActiveSide('back');
                      setZoomLevel(1);
                    }}
                    className={`px-3 py-1 rounded-lg font-bold transition cursor-pointer ${
                      activeSide === 'back' ? 'bg-purple-600 text-white' : 'text-slate-400 hover:text-white'
                    }`}
                  >
                    Back Side
                  </button>
                </div>

                {/* Zoom / Rotate Controls */}
                <div className="flex items-center gap-1 bg-slate-900 border border-slate-800 rounded-xl p-1">
                  <button
                    type="button"
                    onClick={() => setZoomLevel((z) => Math.min(z + 0.25, 3))}
                    className="p-1 hover:text-purple-400 transition"
                    title="Zoom in"
                  >
                    <ZoomIn className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setZoomLevel((z) => Math.max(z - 0.25, 0.75))}
                    className="p-1 hover:text-purple-400 transition"
                    title="Zoom out"
                  >
                    <ZoomOut className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setRotateAngle((r) => (r + 90) % 360)}
                    className="p-1 hover:text-purple-400 transition"
                    title="Rotate 90deg"
                  >
                    <RotateCw className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setZoomLevel(1);
                      setRotateAngle(0);
                    }}
                    className="text-[10px] font-mono font-bold px-1.5 py-0.5 text-slate-400 hover:text-white"
                    title="Reset view"
                  >
                    100%
                  </button>
                </div>
              </div>
            </div>

            {/* Document Viewer Canvas */}
            <div className="flex-1 bg-slate-950 p-4 flex flex-col items-center justify-center overflow-auto min-h-[340px] relative">
              {inspectedSideUrl ? (
                <div
                  className="transition-transform duration-150 flex items-center justify-center"
                  style={{
                    transform: `scale(${zoomLevel}) rotate(${rotateAngle}deg)`,
                  }}
                >
                  <img
                    src={inspectedSideUrl}
                    alt={`${activeDocType} ${activeSide}`}
                    className="max-h-[380px] max-w-full rounded-xl object-contain border border-slate-800 shadow-2xl"
                  />
                </div>
              ) : (
                <div className="text-center space-y-2 text-slate-500 py-12">
                  <FileWarning className="w-12 h-12 mx-auto text-slate-600" />
                  <p className="text-xs font-bold text-slate-400">
                    No {activeSide} image uploaded for {activeDocType.toUpperCase()}
                  </p>
                  <p className="text-[11px] text-slate-500 max-w-xs mx-auto">
                    The companion has not provided this document side or this document is optional.
                  </p>
                </div>
              )}

              {/* Document Metadata Overlay */}
              {inspectedDocRecord && (
                <div className="absolute bottom-3 left-3 bg-slate-900/90 backdrop-blur-md border border-slate-800 rounded-xl px-3 py-2 text-[11px] space-y-0.5">
                  <div className="text-slate-400">
                    ID Number: <span className="text-white font-mono font-bold">{inspectedDocRecord.idNumber || 'N/A'}</span>
                  </div>
                  <div className="text-slate-400">
                    Legal Name on Card: <span className="text-white font-bold">{inspectedDocRecord.legalName || inspectCompanion.name}</span>
                  </div>
                  <div className="text-slate-400">
                    Status:{' '}
                    <span
                      className={`font-mono font-bold ${
                        inspectedDocRecord.status === 'verified'
                          ? 'text-emerald-400'
                          : inspectedDocRecord.status === 'reupload_required'
                          ? 'text-rose-400'
                          : 'text-amber-400'
                      }`}
                    >
                      {inspectedDocRecord.status?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Rejection Form Box (Expandable) */}
            {showRejectBox && (
              <div className="p-4 bg-slate-950 border-t border-rose-900/60 space-y-3 animate-in slide-in-from-bottom-2 duration-150">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-rose-300 flex items-center gap-1.5">
                    <AlertTriangle className="w-4 h-4 text-rose-400" />
                    Specify Document Rejection Reason (Required for Companion Feedback)
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowRejectBox(false)}
                    className="text-slate-400 hover:text-white"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-2">
                  <select
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white focus:outline-none focus:border-rose-500"
                  >
                    {PRESET_REJECTION_REASONS.map((r, i) => (
                      <option key={i} value={r}>
                        {r}
                      </option>
                    ))}
                  </select>

                  <input
                    type="text"
                    value={rejectComments}
                    onChange={(e) => setRejectComments(e.target.value)}
                    placeholder="Additional admin comments or instructions for companion..."
                    className="w-full bg-slate-900 border border-slate-700 rounded-xl px-3 py-2 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-rose-500"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-1">
                  <button
                    type="button"
                    onClick={() => setShowRejectBox(false)}
                    className="px-3 py-1.5 rounded-xl bg-slate-800 text-slate-300 text-xs font-bold hover:bg-slate-700 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    disabled={isReviewSubmitting}
                    onClick={() => handleRejectSingleDoc(inspectCompanion.companionId, activeDocType)}
                    className="px-4 py-1.5 rounded-xl bg-rose-600 hover:bg-rose-500 text-white text-xs font-bold transition flex items-center gap-1.5 cursor-pointer"
                  >
                    {isReviewSubmitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : null}
                    <span>Confirm Rejection & Update Firestore</span>
                  </button>
                </div>
              </div>
            )}

            {/* Modal Footer Review Action Bar */}
            <div className="p-4 bg-slate-950 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-slate-400">
                Action recorded to Firestore audit log: <span className="text-purple-300 font-mono">/verificationEvents</span>
              </div>

              <div className="flex items-center gap-2">
                {!showRejectBox && (
                  <button
                    type="button"
                    onClick={() => setShowRejectBox(true)}
                    className="px-3.5 py-2 rounded-xl bg-rose-950/80 hover:bg-rose-900 border border-rose-800 text-rose-300 text-xs font-bold transition cursor-pointer flex items-center gap-1.5"
                  >
                    <AlertTriangle className="w-3.5 h-3.5" />
                    <span>Reject / Request Re-upload</span>
                  </button>
                )}

                <button
                  type="button"
                  disabled={isReviewSubmitting}
                  onClick={() => handleApproveSingleDoc(inspectCompanion.companionId, activeDocType)}
                  className="px-4 py-2 rounded-xl bg-purple-600 hover:bg-purple-500 text-white text-xs font-bold transition cursor-pointer flex items-center gap-1.5 shadow-md shadow-purple-950"
                >
                  {isReviewSubmitting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  <span>Approve This Document</span>
                </button>

                <button
                  type="button"
                  disabled={isReviewSubmitting}
                  onClick={() => handleDirectApproveEntireKyc(inspectCompanion.companionId)}
                  className="px-4 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold transition cursor-pointer flex items-center gap-1.5 shadow-md shadow-emerald-950"
                >
                  <Check className="w-3.5 h-3.5" />
                  <span>Verify Entire Profile</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
